import React, { useRef, useState, useCallback } from "react";
import {
  Upload,
  X,
  Loader2,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  Paperclip,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { tusUploadFile } from "@/ajax/upload/tusUploadFile";
import {
  declareUploadFile,
  type DeclaredUploadFile,
} from "@/ajax/upload/declare-upload-file";
import {
  ALLOWED_FILE_EXTENSIONS,
  MAX_FILE_ATTACHMENT_SIZE_BYTES,
} from "@api/shared-runtime/file-attachment";

const ACCEPT_ATTRIBUTE = ALLOWED_FILE_EXTENSIONS.join(",");
const IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

export interface UploadedFile {
  fileUploadId: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
}

interface FileUploadInputProps {
  /** Already uploaded files */
  files: UploadedFile[];
  /** Called when a file finishes uploading */
  onFileUploaded: (file: UploadedFile) => void;
  /** Called when a file is removed */
  onFileRemoved: (fileUploadId: string) => void;
  /**
   * Create an upload record and return its storage location plus the
   * server-issued token that authorizes the TUS upload to it.
   */
  createUpload: (file: DeclaredUploadFile) => Promise<{
    id: string;
    storagePath: string;
    bucketName: string;
    uploadToken: string;
  }>;
  /** Maximum number of files (default 10) */
  maxFiles?: number;
  /** Whether to allow multiple files (default true) */
  multiple?: boolean;
  /** Disable the input */
  disabled?: boolean;
  /** Compact mode for inline use */
  compact?: boolean;
}

interface PendingUpload {
  id: string;
  fileName: string;
  status: "uploading" | "error";
  error?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string) {
  if (IMAGE_TYPES.has(mimeType)) {
    return <ImageIcon size={14} className="text-blue-500" />;
  }
  return <FileText size={14} className="text-gray-500" />;
}

export function FileUploadInput({
  files,
  onFileUploaded,
  onFileRemoved,
  createUpload,
  maxFiles = 10,
  multiple = true,
  disabled = false,
  compact = false,
}: FileUploadInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const uploadFile = useCallback(
    async (file: File) => {
      if (file.size > MAX_FILE_ATTACHMENT_SIZE_BYTES) {
        return;
      }

      const pendingId = crypto.randomUUID();
      setPendingUploads((prev) => [
        ...prev,
        { id: pendingId, fileName: file.name, status: "uploading" },
      ]);

      try {
        // Step 1: Create the file upload record
        const declared = await declareUploadFile(file);
        const result = await createUpload(declared);

        // Step 2: Upload the file to storage via TUS, under the reserved type
        await tusUploadFile({
          file,
          bucketName: result.bucketName,
          objectName: result.storagePath,
          uploadToken: result.uploadToken,
        });

        // Step 3: Notify parent
        onFileUploaded({
          fileUploadId: result.id,
          fileName: file.name,
          mimeType: declared.mimeType,
          fileSizeBytes: file.size,
        });

        // Remove from pending
        setPendingUploads((prev) => prev.filter((p) => p.id !== pendingId));
      } catch (error) {
        setPendingUploads((prev) =>
          prev.map((p) =>
            p.id === pendingId
              ? {
                  ...p,
                  status: "error" as const,
                  error:
                    error instanceof Error ? error.message : "Upload failed",
                }
              : p,
          ),
        );
      }
    },
    [createUpload, onFileUploaded],
  );

  const processFiles = useCallback(
    (fileList: FileList | File[]) => {
      const remaining = maxFiles - files.length - pendingUploads.length;
      const filesToUpload = Array.from(fileList).slice(0, remaining);
      filesToUpload.forEach(uploadFile);
    },
    [files.length, pendingUploads.length, maxFiles, uploadFile],
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const removePending = (pendingId: string) => {
    setPendingUploads((prev) => prev.filter((p) => p.id !== pendingId));
  };

  const canAddMore = files.length + pendingUploads.length < maxFiles;
  const isUploading = pendingUploads.some((p) => p.status === "uploading");
  const hasFiles = files.length > 0 || pendingUploads.length > 0;

  return (
    <div className="space-y-2">
      {/* File list */}
      {hasFiles && (
        <div className="space-y-1.5">
          {files.map((file) => (
            <div
              key={file.fileUploadId}
              className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-100 text-sm group"
            >
              {getFileIcon(file.mimeType)}
              <span className="flex-1 min-w-0 truncate text-gray-700">
                {file.fileName}
              </span>
              <span className="text-xs text-gray-400 shrink-0">
                {formatFileSize(file.fileSizeBytes)}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onFileRemoved(file.fileUploadId)}
                  className="text-gray-300 hover:text-red-500 shrink-0 p-0.5"
                  aria-label="Remove file"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}

          {pendingUploads.map((pending) => (
            <div
              key={pending.id}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-sm ${
                pending.status === "error"
                  ? "bg-red-50 border-red-100"
                  : "bg-blue-50 border-blue-100"
              }`}
            >
              {pending.status === "uploading" ? (
                <Loader2 size={14} className="text-blue-500 animate-spin" />
              ) : (
                <AlertCircle size={14} className="text-red-500" />
              )}
              <span className="flex-1 min-w-0 truncate text-gray-700">
                {pending.fileName}
              </span>
              {pending.status === "uploading" && (
                <span className="text-xs text-blue-500 shrink-0">
                  Uploading...
                </span>
              )}
              {pending.status === "error" && (
                <>
                  <span
                    className="text-xs text-red-500 shrink-0 truncate max-w-[12rem]"
                    title={pending.error ?? "Failed"}
                  >
                    {pending.error ?? "Failed"}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePending(pending.id)}
                    className="text-red-300 hover:text-red-500 shrink-0 p-0.5"
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      {canAddMore && !disabled && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            multiple={multiple}
            onChange={handleFileInputChange}
            className="hidden"
          />

          {compact ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-8"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Paperclip size={12} className="mr-1.5" />
              Attach file{multiple ? "s" : ""}
            </Button>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <Upload size={20} className="mx-auto mb-1.5 text-gray-400" />
              <p className="text-sm text-gray-500">
                Drop files here or{" "}
                <span className="text-blue-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                PDF, images, documents up to 20MB
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
